import { saveAs } from 'file-saver';
import {
    createUseSearchService,
    DependenciesWatcher,
    ISearchConfiguration,
    ISearchRequest,
    ISearchResponse,
    ISearchService,
    SearchCriteriaError,
} from '../../../reusableFeatures';
import { backendServices, Currency, orderBy, portalUserService } from '../../../services';
import { calculatePageOffset, isValidDateRange, setRelativeComplement } from '../../../utils';
import { BusinessDocType } from '../../../types';
import { DocumentSearchConfigurationUtils, multiCompanySelectDefaultFieldConfig } from '../..';
import { DocumentSearchConfigurationPage } from '../../DocumentSearchConfiguration';

export class DocumentSearchService implements ISearchService<DocumentSearchConfigurationUtils.FilterValues> {
    private documentApi = new backendServices.Apis.DocumentApi();

    private getCurrentConfiguration(vm: backendServices.ViewModels.DocumentSearchConfigurationViewModel[]) {
        return vm.find((conf) => conf.ScreenSize === this.layoutSize)!;
    }

    private multiCompanyIsAvailable = false;

    constructor(
        private readonly businessDocType: BusinessDocType,
        private readonly isBuyerPortal: boolean,
        private readonly layoutSize: backendServices.ViewModels.FieldDictionaryScreenSize,
        private readonly currencyList: Currency[],
        private readonly dependenciesWatcher: DependenciesWatcher,
        private readonly page: number,
        private readonly onFetchSearchConfigurationSuccess: (
            config: backendServices.ViewModels.DocumentSearchConfigurationViewModel
        ) => void,
        private readonly currentUserGroups: Pick<
            backendServices.ViewModels.UserGroupViewModel,
            'Id' | 'Name' | 'CompanyId'
        >[],
        private readonly canUserBypassUserGroupODataFilter: boolean,
        private readonly searchConfigurationId: number | undefined,
        /** Extra OData clause ANDed onto every search, e.g. scoping a page to one vendor. */
        private readonly additionalFilter: string | undefined
    ) {
        this.multiCompanyIsAvailable = isBuyerPortal && portalUserService.getCompanyChildren().length > 0;
    }

    async fetchSearchConfiguration(): Promise<ISearchConfiguration<DocumentSearchConfigurationUtils.FilterValues>> {
        const [, { data: configurationVM }] = await Promise.all([
            this.dependenciesWatcher.waitForDependencies(),
            this.searchConfigurationId !== undefined
                ? this.documentApi.getDocumentSearchConfigurationById(this.searchConfigurationId, {
                      includeAllScreenSizes: true,
                      isBuyerPortal: true, // this scenario only occurs in buyer portal
                  })
                : this.documentApi.getDocumentSearchConfiguration({
                      businessDocType: this.businessDocType,
                      isBuyerPortal: this.isBuyerPortal,
                      page: this.page,
                      workflowTemplateUI: null,
                  }),
        ]);
        const currentConfiguration = this.getCurrentConfiguration(configurationVM);

        this.onFetchSearchConfigurationSuccess(currentConfiguration);

        const searchFormFieldColumns = DocumentSearchConfigurationUtils.getSearchFormFieldColumns({
            configuration: currentConfiguration,
            allowMultipleCompanies: false, // TODO: temporary fix for MultiCompanySelector not supporting multiple companies in Transactions
            isParkingLot: false,
        });

        const gridColumns = DocumentSearchConfigurationUtils.getGridColumns({
            configuration: currentConfiguration,
            currencyList: this.currencyList,
            /**
             * This is fine, {users} and {userGroups} are only used in parking lot
             */
            users: [],
            userGroups: [],
        });

        return {
            gridColumns,
            enabledExportingMethods: [],
            searchFormFieldColumns,
            pageSizeOptions: [10, 25, 50],
            defaultPageSize: 10,
            searchWithNoCriteria: this.page === DocumentSearchConfigurationPage.SUPPLIER_SEARCH,
            initialSort: [
                {
                    field:
                        this.page === DocumentSearchConfigurationPage.INVOICES_REJECTED
                            ? DocumentSearchConfigurationUtils.constants.SENDER_CONFIRMATION_FIELD_KEY
                            : this.page === DocumentSearchConfigurationPage.INVOICES_DELIVERED
                            ? DocumentSearchConfigurationUtils.constants.DISPATCHED_TIME_FIELD_KEY
                            : DocumentSearchConfigurationUtils.constants.CREATED_TIME_FIELD_KEY,
                    sort: 'desc',
                },
            ],
            viewModel: configurationVM,
            isCriteriaEmpty: (searchQuery, filledFields) => {
                const multiCompanySelectEmpty = !filledFields.includes(
                    multiCompanySelectDefaultFieldConfig.dataBindingKey
                );

                if (this.multiCompanyIsAvailable && multiCompanySelectEmpty) {
                    return true;
                }

                const dateRange = searchQuery.getValueWithKey(
                    DocumentSearchConfigurationUtils.constants.CREATED_TIME_FIELD_KEY
                );

                const dateRangeIsEmpty = !isValidDateRange(dateRange);

                const otherFields = setRelativeComplement(filledFields, [
                    multiCompanySelectDefaultFieldConfig.dataBindingKey,
                    DocumentSearchConfigurationUtils.constants.CREATED_TIME_FIELD_KEY,
                ]);

                return otherFields.length === 0 && dateRangeIsEmpty;
            },

            determineDefaultCollapsedAdvancedSearch: (searchQuery, filledFields) => {
                const irrelevantFields = [multiCompanySelectDefaultFieldConfig.dataBindingKey];

                const relevantFields = setRelativeComplement(filledFields, irrelevantFields);

                return relevantFields.length === 0;
            },
        };
    }

    async fetchResults(
        searchConfiguration: ISearchConfiguration<
            DocumentSearchConfigurationUtils.FilterValues,
            backendServices.ViewModels.DocumentSearchConfigurationViewModel[]
        >,
        searchRequest: ISearchRequest<DocumentSearchConfigurationUtils.FilterValues>
    ): Promise<ISearchResponse> {
        DocumentSearchConfigurationUtils.removeSuffixForDupesFromSearchRequest(searchRequest);

        const currentConfiguration = this.getCurrentConfiguration(searchConfiguration.viewModel!);

        const { $filter: initialFilter, dynamicParams } = DocumentSearchConfigurationUtils.getSearchFilterAndParams(
            searchRequest,
            currentConfiguration,
            this.businessDocType,
            []
        );

        let $filter = initialFilter;

        // if the current user has a special permission to bypass the user group odata filter, return null. Otherwise, get the user group odata filter
        const userGroupODataFilter = this.canUserBypassUserGroupODataFilter
            ? null
            : DocumentSearchConfigurationUtils.getUserGroupODataFilter(
                  currentConfiguration.SearchConfiguration,
                  this.currentUserGroups
              );

        if (userGroupODataFilter) {
            $filter = `(${$filter}) and (${userGroupODataFilter})`;
        }

        if (this.additionalFilter) {
            $filter = `(${$filter}) and (${this.additionalFilter})`;
        }

        const selectedChildCompanies =
            (searchRequest.searchQuery.state[multiCompanySelectDefaultFieldConfig.dataBindingKey] as string[]) ?? [];

        try {
            const { data } = await this.documentApi.getDocuments(
                {
                    $count: true,
                    $filter,
                    $skip: calculatePageOffset(searchRequest.pageSize, searchRequest.pageNumber),
                    $top: searchRequest.pageSize,
                    companyID: portalUserService.getCurrentCompanyId(),
                    $orderby: orderBy(searchRequest.sort),
                    isBuyer: this.isBuyerPortal,
                    childCompanyId: selectedChildCompanies.join(','),
                },
                dynamicParams
            );

            return {
                pageResults: data.Items.map((item) => ({ id: `${item.Id}-${item.DocumentID}`, ...item })),
                totalResultCount: data.Count ?? 0,
            };
        } catch (error: any) {
            console.error(error);
            if (error?.response?.status >= 400 && error?.response?.status <= 599) {
                throw new SearchCriteriaError(
                    'Network error. Please wait a few minutes and try again. If you continue to receive this error please contact Transcepta Support at support@transcepta.com.'
                );
            }

            throw error;
        }
    }

    async exportData(
        searchConfiguration: ISearchConfiguration<
            DocumentSearchConfigurationUtils.FilterValues,
            backendServices.ViewModels.DocumentSearchConfigurationViewModel[]
        >,
        searchRequest: ISearchRequest<DocumentSearchConfigurationUtils.FilterValues>,
        _exportMethod: string,
        filename: string
    ) {
        DocumentSearchConfigurationUtils.removeSuffixForDupesFromSearchRequest(searchRequest);

        const currentConfiguration = this.getCurrentConfiguration(searchConfiguration.viewModel!);

        const { $filter: initialFilter, dynamicParams } = DocumentSearchConfigurationUtils.getSearchFilterAndParams(
            searchRequest,
            currentConfiguration,
            this.businessDocType,
            []
        );

        let $filter = initialFilter;

        const userGroupODataFilter = this.canUserBypassUserGroupODataFilter
            ? null
            : DocumentSearchConfigurationUtils.getUserGroupODataFilter(
                  currentConfiguration.SearchConfiguration,
                  this.currentUserGroups
              );

        if (userGroupODataFilter) {
            $filter = `(${$filter}) and (${userGroupODataFilter})`;
        }

        if (this.additionalFilter) {
            $filter = `(${$filter}) and (${this.additionalFilter})`;
        }

        const selectedChildCompanies =
            (searchRequest.searchQuery.state[multiCompanySelectDefaultFieldConfig.dataBindingKey] as string[]) ?? [];

        const { data } = await this.documentApi.getDocumentsCSV(
            {
                $filter,
                companyID: portalUserService.getCurrentCompanyId(),
                $orderby: orderBy(searchRequest.sort),
                isBuyer: this.isBuyerPortal,
                childCompanyId: selectedChildCompanies.join(','),
            },
            dynamicParams
        );

        const blob = new Blob([data], { type: 'text/csv;charset=utf-8' });
        saveAs(blob, filename);
    }

    get key(): string {
        const composedKey = [
            'DocumentSearchPage',
            this.businessDocType.toString(),
            this.layoutSize.toString(),
            this.page,
        ];

        // Only appended when set, so keys for unscoped pages stay unchanged; keeps cached
        // results and stored page size from leaking between vendors on a scoped page.
        if (this.additionalFilter) {
            composedKey.push(this.additionalFilter);
        }

        return composedKey.join();
    }
}

export const useDocumentSearchService = createUseSearchService(DocumentSearchService);
